import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Percent, Shield, Headphones, Plus, X, ArrowLeftRight } from 'lucide-react';
import { Tabs, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { useAccount } from '../context/AccountContext';
import { api } from '../lib/api';
import { useModulesConfig } from '../lib/modules';
import HotelSearchPanel from './search/HotelSearchPanel';
import { DiscoveryThemes, DiscoveryTestimonials } from './discovery/Discovery';
import { Section, Carousel, SkeletonCard } from './discovery/Primitives';
import { RecentSearchesStrip, RecentlyViewedHotels, RecentlyViewedFlights } from './discovery/RecentRails';
import TrendingStays from './discovery/TrendingStays';
import ProductTabsBar from './search/ProductTabsBar';
import { track, Events } from '../lib/analytics';
import { recordSearch } from '../lib/recent';

// Helpers ---------------------------------------------------------------
const today = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
};

const buildSearchUrl = (type, params) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      sp.set(k, String(v).trim());
    }
  });
  const qs = sp.toString();
  return `/${type}/search${qs ? `?${qs}` : ''}`;
};

// Default state per tab — mirrors searchModules.js defaults
const initialState = {
  hotels: { city: '', check_in: today(7), check_out: today(9), guests: 2, rooms: 1 },
  flights: { trip_type: 'oneway', origin: 'DEL', destination: 'BOM', departure_date: today(7), return_date: today(10), adults: 1, cabin_class: 'Economy', fare_type: 'regular', segments: [
    { origin: 'DEL', destination: 'BOM', departure_date: today(7) },
    { origin: 'BOM', destination: 'BLR', departure_date: today(10) },
  ] },
  trains: { origin: 'Delhi', destination: 'Mumbai', travel_date: today(7) },
  buses: { origin: 'Delhi', destination: 'Jaipur', travel_date: today(7), passengers: 1 },
  cabs: { pickup: '', drop: '', pickup_date: today(1), trip_type: 'Local' },
  villas: { city: '', check_in: today(7), check_out: today(9), guests: 2 },
  packages: { destination: '', start_date: today(14), travellers: 2 },
  tours: { city: '', tour_date: today(3), travellers: 2 },
  visa: { country: '', travel_date: today(30) },
  cruise: { destination: '', sail_date: today(21), guests: 2 },
  forex: { currency: 'USD', amount: 1000 },
  insurance: { destination: '', travel_date: today(14), travellers: 1 },
};

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Pre-select tab from `?tab=hotels` URL param (used when user clicks a module in navbar).
  const initialTab = new URLSearchParams(location.search).get('tab') || 'hotels';
  const [activeTab, setActiveTab] = useState(initialTab);
  const { accountType, theme } = useAccount();
  const [offers, setOffers] = useState([]);
  const [popularDestinations, setPopularDestinations] = useState([]);
  const [forms, setForms] = useState(initialState);
  const { modules: modulesEnabled, order: moduleOrder } = useModulesConfig();

  // Keep tab in sync if user clicks another navbar module while already on /.
  useEffect(() => {
    const t = new URLSearchParams(location.search).get('tab');
    if (t && t !== activeTab) setActiveTab(t);
  }, [location.search]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    track(Events.HOME_VIEW, { account_type: accountType });
    api.get('/services/offers').then((r) => setOffers(r.data)).catch(() => {});
    api.get('/services/destinations').then((r) => setPopularDestinations(r.data)).catch(() => {});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const updateField = (type, field, value) => {
    setForms((prev) => {
      const next = { ...prev, [type]: { ...prev[type], [field]: value } };
      // Auto-bump return_date to stay >= departure_date in flight round-trip mode
      if (type === 'flights' && field === 'departure_date' && next.flights.return_date && next.flights.return_date <= value) {
        const d = new Date(value);
        d.setDate(d.getDate() + 3);
        next.flights.return_date = d.toISOString().slice(0, 10);
      }
      return next;
    });
  };

  const handleSearch = (type) => {
    track(Events.SEARCH_INITIATED, { module: type, query: forms[type] });
    let url;
    let label = '';
    let sublabel = '';
    if (type === 'flights') {
      const f = forms.flights;
      const params = { trip_type: f.trip_type, adults: f.adults, cabin_class: f.cabin_class, fare_type: f.fare_type };
      if (f.trip_type === 'multi') {
        const segs = (f.segments || []).filter((s) => s.origin && s.destination && s.departure_date);
        params.segments = segs.map((s) => `${s.origin}-${s.destination}-${s.departure_date}`).join(',');
        label = `Multi-city · ${segs.length} legs`;
        sublabel = segs.map((s) => `${s.origin}→${s.destination}`).join(' • ');
      } else {
        params.origin = f.origin;
        params.destination = f.destination;
        params.departure_date = f.departure_date;
        if (f.trip_type === 'round') params.return_date = f.return_date;
        label = `${f.origin} → ${f.destination}`;
        sublabel = f.trip_type === 'round' ? `${f.departure_date} · return ${f.return_date}` : `${f.departure_date}`;
      }
      url = buildSearchUrl(type, params);
    } else {
      url = buildSearchUrl(type, forms[type]);
      const f = forms[type];
      if (type === 'hotels') {
        label = f.city || 'Hotels';
        sublabel = `${f.check_in} → ${f.check_out} · ${f.guests || 2} guests`;
      } else if (type === 'buses' || type === 'trains' || type === 'cabs') {
        label = `${f.origin || f.pickup || '—'} → ${f.destination || f.drop || '—'}`;
        sublabel = f.travel_date || f.pickup_date || '';
      } else {
        label = f.destination || f.country || f.city || type;
        sublabel = f.travel_date || f.start_date || f.tour_date || f.sail_date || '';
      }
    }
    if (label) recordSearch({ module: type, label, sublabel, url });
    navigate(url);
  };

  const onSelectTab = (slug) => {
    if (modulesEnabled[slug] !== true) return;
    setActiveTab(slug);
  };

  const personalBg = 'https://images.unsplash.com/photo-1603477849227-705c424d1d80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHx0cm9waWNhbCUyMGJlYWNofGVufDB8fHx8MTc3Njg4MzM4NXww&ixlib=rb-4.1.0&q=85';
  const businessBg = 'https://images.unsplash.com/photo-1602190276434-4cd5500bc6fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHRyYXZlbHxlbnwwfHx8fDE3NzY4ODMzODh8MA&ixlib=rb-4.1.0&q=85';

  const searchBtnCls = `w-full h-12 sm:h-14 bg-gradient-to-r ${theme.primary} hover:${theme.primaryHover} text-white font-bold text-sm sm:text-base rounded-lg shadow-lg`;
  const labelCls = 'block text-[11px] sm:text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2 uppercase';
  const inputCls = 'h-12 sm:h-14 text-sm sm:text-base font-medium border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white bg-white dark:bg-gray-800 placeholder:text-gray-400 dark:placeholder:text-gray-500';
  const dateInputCls = 'h-12 sm:h-14 text-sm sm:text-base border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white bg-white dark:bg-gray-800 [color-scheme:light] dark:[color-scheme:dark]';

  // Flight trip-type helpers
  const updateSegment = (idx, field, value) => {
    setForms((prev) => {
      const segs = [...(prev.flights.segments || [])];
      segs[idx] = { ...segs[idx], [field]: value };
      return { ...prev, flights: { ...prev.flights, segments: segs } };
    });
  };
  const addSegment = () => {
    setForms((prev) => {
      const segs = [...(prev.flights.segments || [])];
      const last = segs[segs.length - 1] || { origin: '', destination: '', departure_date: today(7) };
      segs.push({ origin: last.destination || '', destination: '', departure_date: today(14 + segs.length) });
      return { ...prev, flights: { ...prev.flights, segments: segs } };
    });
  };
  const removeSegment = (idx) => {
    setForms((prev) => {
      const segs = (prev.flights.segments || []).filter((_, i) => i !== idx);
      return { ...prev, flights: { ...prev.flights, segments: segs.length >= 2 ? segs : prev.flights.segments } };
    });
  };
  const setTripType = (t) => updateField('flights', 'trip_type', t);
  const swapFlightCities = () => setForms((prev) => ({
    ...prev,
    flights: { ...prev.flights, origin: prev.flights.destination, destination: prev.flights.origin },
  }));

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200">
      {/* Sticky bar removed — navbar itself shows modules in center when scrolled (see Navbar.jsx). */}

      {/* ─── Hero Section — image extends BEHIND the transparent navbar ───
          Note: Navbar is `fixed` & `bg-transparent` on '/' so we add the navbar
          height (h-14 mobile / h-16 desktop) as a top offset for hero content
          but the IMAGE/gradient still spans the full viewport-top. */}
      <div
        className="relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${accountType === 'personal' ? personalBg : businessBg})` }}
      >
        {/* Diagonal gradient overlay — keeps headline readable while letting image breathe */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/55 dark:from-black/70 dark:via-black/50 dark:to-black/70" />

        {/* Pad-top so headline starts below floating navbar (h-14 / h-16). Compact hero — no scroll required to reach search. */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-24 pb-5 sm:pb-7">
          {/* ─── BOX 1: Functions card (icons only, MMT style) — hugs content width ─── */}
          <div className="flex justify-center mb-3 sm:mb-4">
            <Card className="bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden border-0 dark:border dark:border-gray-700 inline-block" data-testid="functions-card">
              <CardContent className="p-0">
                <ProductTabsBar
                  active={activeTab}
                  onSelect={onSelectTab}
                  modulesEnabled={modulesEnabled}
                  order={moduleOrder}
                  hug
                />
              </CardContent>
            </Card>
          </div>

          {/* ─── BOX 2: Search card (no overflow-hidden so autocomplete dropdowns are not clipped) ─── */}
          <Card className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border-0 dark:border dark:border-gray-700 relative" data-testid="search-card">
            <CardContent className="p-0">
              <Tabs value={activeTab} onValueChange={onSelectTab} className="w-full">
                <div className="hidden">
                  <ProductTabsBar
                    active={activeTab}
                    onSelect={onSelectTab}
                    modulesEnabled={modulesEnabled}
                    order={moduleOrder}
                  />
                </div>

                <div className="p-4 sm:p-5 md:p-6">
                  <TabsContent value="flights" className="mt-0">
                    {/* Trip type selector */}
                    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="flight-trip-types">
                      {[
                        { v: 'oneway',  label: 'One Way' },
                        { v: 'round',   label: 'Round Trip' },
                        { v: 'multi',   label: 'Multi City' },
                      ].map((t) => {
                        const active = forms.flights.trip_type === t.v;
                        return (
                          <button
                            key={t.v}
                            type="button"
                            data-testid={`flight-trip-${t.v}`}
                            onClick={() => setTripType(t.v)}
                            className={`px-3.5 py-1.5 rounded-full text-xs sm:text-[13px] font-semibold transition-all border ${
                              active
                                ? 'bg-red-600 text-white border-red-600 shadow-sm'
                                : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-700 hover:border-red-500 hover:text-red-600'
                            }`}
                          >
                            <span className={`inline-block h-2 w-2 rounded-full mr-1.5 align-middle ${active ? 'bg-white' : 'bg-gray-300 dark:bg-gray-600'}`} />
                            {t.label}
                          </button>
                        );
                      })}
                    </div>

                    {forms.flights.trip_type !== 'multi' ? (
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4">
                        <div className="md:col-span-2 relative">
                          <label className={labelCls}>From</label>
                          <Input data-testid="flights-origin" placeholder="DEL" maxLength={3} value={forms.flights.origin} onChange={(e) => updateField('flights', 'origin', e.target.value.toUpperCase())} className={inputCls} />
                          <button
                            type="button"
                            onClick={swapFlightCities}
                            aria-label="Swap origin and destination"
                            title="Swap"
                            data-testid="flights-swap-btn"
                            className="hidden md:grid absolute -right-6 top-[34px] z-10 h-8 w-8 place-items-center rounded-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 shadow-md hover:bg-blue-50 dark:hover:bg-blue-900/40 hover:scale-110 active:scale-95 transition-all"
                          >
                            <ArrowLeftRight className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="md:col-span-2">
                          <label className={labelCls}>To</label>
                          <Input data-testid="flights-destination" placeholder="BOM" maxLength={3} value={forms.flights.destination} onChange={(e) => updateField('flights', 'destination', e.target.value.toUpperCase())} className={inputCls} />
                        </div>
                        <div className={forms.flights.trip_type === 'round' ? 'md:col-span-2' : 'md:col-span-2'}>
                          <label className={labelCls}>Departure</label>
                          <Input data-testid="flights-date" type="date" value={forms.flights.departure_date} onChange={(e) => updateField('flights', 'departure_date', e.target.value)} className={dateInputCls} />
                        </div>
                        {forms.flights.trip_type === 'round' && (
                          <div className="md:col-span-2">
                            <label className={labelCls}>Return</label>
                            <Input data-testid="flights-return-date" type="date" value={forms.flights.return_date} onChange={(e) => updateField('flights', 'return_date', e.target.value)} className={dateInputCls} />
                          </div>
                        )}
                        <div className={forms.flights.trip_type === 'round' ? 'md:col-span-1' : 'md:col-span-2'}>
                          <label className={labelCls}>Travellers</label>
                          <Input data-testid="flights-adults" type="number" min={1} max={9} value={forms.flights.adults} onChange={(e) => updateField('flights', 'adults', e.target.value)} className={inputCls} />
                        </div>
                        <div className={forms.flights.trip_type === 'round' ? 'md:col-span-1' : 'md:col-span-2'}>
                          <label className={labelCls}>Class</label>
                          <select
                            data-testid="flights-class"
                            value={forms.flights.cabin_class}
                            onChange={(e) => updateField('flights', 'cabin_class', e.target.value)}
                            className={`${inputCls} px-3 rounded-md border w-full`}
                          >
                            <option value="Economy">Economy</option>
                            <option value="Premium Economy">Premium</option>
                            <option value="Business">Business</option>
                            <option value="First">First</option>
                          </select>
                        </div>
                        <div className="md:col-span-2 flex items-end">
                          <Button data-testid="search-flights-btn" onClick={() => handleSearch('flights')} className={searchBtnCls}>
                            <Search className="mr-2 h-5 w-5" /> SEARCH
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3" data-testid="flight-multi-rows">
                        {forms.flights.segments.map((seg, idx) => (
                          <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end" data-testid={`flight-segment-${idx}`}>
                            <div className="md:col-span-3">
                              <label className={labelCls}>From · Trip {idx + 1}</label>
                              <Input data-testid={`flight-seg-${idx}-origin`} placeholder="DEL" maxLength={3} value={seg.origin} onChange={(e) => updateSegment(idx, 'origin', e.target.value.toUpperCase())} className={inputCls} />
                            </div>
                            <div className="md:col-span-3">
                              <label className={labelCls}>To</label>
                              <Input data-testid={`flight-seg-${idx}-destination`} placeholder="BOM" maxLength={3} value={seg.destination} onChange={(e) => updateSegment(idx, 'destination', e.target.value.toUpperCase())} className={inputCls} />
                            </div>
                            <div className="md:col-span-3">
                              <label className={labelCls}>Departure</label>
                              <Input data-testid={`flight-seg-${idx}-date`} type="date" value={seg.departure_date} onChange={(e) => updateSegment(idx, 'departure_date', e.target.value)} className={dateInputCls} />
                            </div>
                            <div className="md:col-span-3 flex gap-2">
                              {forms.flights.segments.length > 2 && (
                                <Button type="button" variant="outline" data-testid={`flight-seg-${idx}-remove`} onClick={() => removeSegment(idx)} className="h-12 sm:h-14 px-3 border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:text-red-600">
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                        <div className="flex flex-wrap items-end gap-3 pt-1">
                          <Button type="button" variant="outline" data-testid="flight-add-segment" onClick={addSegment} disabled={forms.flights.segments.length >= 5} className="h-11 border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 font-semibold">
                            <Plus className="h-4 w-4 mr-1.5" /> Add Segment
                          </Button>
                          <div className="flex-1 min-w-[140px]">
                            <label className={labelCls}>Travellers</label>
                            <Input data-testid="flights-adults" type="number" min={1} max={9} value={forms.flights.adults} onChange={(e) => updateField('flights', 'adults', e.target.value)} className={inputCls} />
                          </div>
                          <div className="flex-1 min-w-[160px]">
                            <label className={labelCls}>Class</label>
                            <select
                              data-testid="flights-class-multi"
                              value={forms.flights.cabin_class}
                              onChange={(e) => updateField('flights', 'cabin_class', e.target.value)}
                              className={`${inputCls} px-3 rounded-md border w-full`}
                            >
                              <option value="Economy">Economy</option>
                              <option value="Premium Economy">Premium</option>
                              <option value="Business">Business</option>
                              <option value="First">First</option>
                            </select>
                          </div>
                          <div className="w-full sm:w-auto sm:min-w-[200px]">
                            <Button data-testid="search-flights-btn" onClick={() => handleSearch('flights')} className={searchBtnCls}>
                              <Search className="mr-2 h-5 w-5" /> SEARCH
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Special Fares row (MMT-style) — card chips with description below each name. */}
                    <div className="mt-4" data-testid="flight-fare-types">
                      <div className="flex items-start gap-3 flex-wrap">
                        <div className="shrink-0 pt-1">
                          <p className="text-[11px] sm:text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200 leading-tight">Special Fares</p>
                          <p className="text-[10px] sm:text-[11px] text-gray-500 dark:text-gray-400 leading-tight">(Optional)</p>
                        </div>
                        <div className="flex flex-wrap gap-2 flex-1">
                          {[
                            { v: 'regular',  label: 'Regular',          desc: 'Regular Fares' },
                            { v: 'student',  label: 'Student',          desc: 'Extra discounts/baggage' },
                            { v: 'armed',    label: 'Armed Forces',     desc: 'Up to ₹600 off' },
                            { v: 'sr',       label: 'Senior Citizen',   desc: 'Up to ₹600 off' },
                            { v: 'doctor',   label: 'Doctor & Nurses',  desc: 'Up to ₹600 off' },
                          ].map((f) => {
                            const active = forms.flights.fare_type === f.v;
                            return (
                              <button
                                key={f.v}
                                type="button"
                                data-testid={`flight-fare-${f.v}`}
                                onClick={() => updateField('flights', 'fare_type', f.v)}
                                className="relative text-left rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 min-w-[140px] transition-all hover:border-red-400 hover:bg-red-50/40 dark:hover:bg-red-900/10"
                              >
                                <div className="flex items-center gap-1.5">
                                  <span className={`inline-block h-3.5 w-3.5 rounded-full border-2 ${active ? 'border-red-500 bg-red-500' : 'border-gray-400 dark:border-gray-500'}`} />
                                  <span className="text-[12px] font-semibold text-gray-900 dark:text-white">{f.label}</span>
                                </div>
                                <p className="mt-0.5 text-[10px] sm:text-[11px] text-gray-500 dark:text-gray-400">{f.desc}</p>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="hotels" className="mt-0">
                    <HotelSearchPanel />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                      Search any city in India or worldwide. We'll show you the best matching properties — even with blank details.
                    </p>
                  </TabsContent>

                  <TabsContent value="trains" className="mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-3"><label className={labelCls}>From</label><Input data-testid="trains-origin" placeholder="Source Station" value={forms.trains.origin} onChange={(e) => updateField('trains', 'origin', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-3"><label className={labelCls}>To</label><Input data-testid="trains-destination" placeholder="Destination Station" value={forms.trains.destination} onChange={(e) => updateField('trains', 'destination', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-4"><label className={labelCls}>Journey Date</label><Input data-testid="trains-date" type="date" value={forms.trains.travel_date} onChange={(e) => updateField('trains', 'travel_date', e.target.value)} className={dateInputCls} /></div>
                      <div className="md:col-span-2 flex items-end"><Button data-testid="search-trains-btn" onClick={() => handleSearch('trains')} className={searchBtnCls}><Search className="mr-2 h-5 w-5" /> SEARCH</Button></div>
                    </div>
                  </TabsContent>

                  <TabsContent value="buses" className="mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-3"><label className={labelCls}>From</label><Input data-testid="buses-origin" placeholder="Source City" value={forms.buses.origin} onChange={(e) => updateField('buses', 'origin', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-3"><label className={labelCls}>To</label><Input data-testid="buses-destination" placeholder="Destination City" value={forms.buses.destination} onChange={(e) => updateField('buses', 'destination', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-2"><label className={labelCls}>Travel Date</label><Input data-testid="buses-date" type="date" value={forms.buses.travel_date} onChange={(e) => updateField('buses', 'travel_date', e.target.value)} className={dateInputCls} /></div>
                      <div className="md:col-span-2"><label className={labelCls}>Passengers</label><Input data-testid="buses-passengers" type="number" min={1} value={forms.buses.passengers} onChange={(e) => updateField('buses', 'passengers', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-2 flex items-end"><Button data-testid="search-buses-btn" onClick={() => handleSearch('buses')} className={searchBtnCls}><Search className="mr-2 h-5 w-5" /> SEARCH</Button></div>
                    </div>
                  </TabsContent>

                  <TabsContent value="cabs" className="mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-4"><label className={labelCls}>Pickup Location</label><Input data-testid="cabs-pickup" placeholder="Enter pickup location" value={forms.cabs.pickup} onChange={(e) => updateField('cabs', 'pickup', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-4"><label className={labelCls}>Drop Location</label><Input data-testid="cabs-drop" placeholder="Enter drop location" value={forms.cabs.drop} onChange={(e) => updateField('cabs', 'drop', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-2"><label className={labelCls}>Pickup Date</label><Input data-testid="cabs-date" type="date" value={forms.cabs.pickup_date} onChange={(e) => updateField('cabs', 'pickup_date', e.target.value)} className={dateInputCls} /></div>
                      <div className="md:col-span-2 flex items-end"><Button data-testid="search-cabs-btn" onClick={() => handleSearch('cabs')} className={searchBtnCls}><Search className="mr-2 h-5 w-5" /> SEARCH</Button></div>
                    </div>
                  </TabsContent>

                  <TabsContent value="villas" className="mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-4"><label className={labelCls}>Destination</label><Input data-testid="villas-city" placeholder="Enter city or location" value={forms.villas.city} onChange={(e) => updateField('villas', 'city', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-3"><label className={labelCls}>Check-in</label><Input data-testid="villas-checkin" type="date" value={forms.villas.check_in} onChange={(e) => updateField('villas', 'check_in', e.target.value)} className={dateInputCls} /></div>
                      <div className="md:col-span-3"><label className={labelCls}>Check-out</label><Input data-testid="villas-checkout" type="date" value={forms.villas.check_out} onChange={(e) => updateField('villas', 'check_out', e.target.value)} className={dateInputCls} /></div>
                      <div className="md:col-span-2 flex items-end"><Button data-testid="search-villas-btn" onClick={() => handleSearch('villas')} className={searchBtnCls}><Search className="mr-2 h-5 w-5" /> SEARCH</Button></div>
                    </div>
                  </TabsContent>

                  <TabsContent value="packages" className="mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-5"><label className={labelCls}>Destination</label><Input data-testid="packages-destination" placeholder="Where do you want to go?" value={forms.packages.destination} onChange={(e) => updateField('packages', 'destination', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-3"><label className={labelCls}>Travel Date</label><Input data-testid="packages-date" type="date" value={forms.packages.start_date} onChange={(e) => updateField('packages', 'start_date', e.target.value)} className={dateInputCls} /></div>
                      <div className="md:col-span-2"><label className={labelCls}>Travellers</label><Input data-testid="packages-travellers" type="number" min={1} value={forms.packages.travellers} onChange={(e) => updateField('packages', 'travellers', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-2 flex items-end"><Button data-testid="search-packages-btn" onClick={() => handleSearch('packages')} className={searchBtnCls}><Search className="mr-2 h-5 w-5" /> SEARCH</Button></div>
                    </div>
                  </TabsContent>

                  <TabsContent value="tours" className="mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-5"><label className={labelCls}>City or Attraction</label><Input data-testid="tours-city" placeholder="Enter destination" value={forms.tours.city} onChange={(e) => updateField('tours', 'city', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-3"><label className={labelCls}>Date</label><Input data-testid="tours-date" type="date" value={forms.tours.tour_date} onChange={(e) => updateField('tours', 'tour_date', e.target.value)} className={dateInputCls} /></div>
                      <div className="md:col-span-2"><label className={labelCls}>Travellers</label><Input data-testid="tours-travellers" type="number" min={1} value={forms.tours.travellers} onChange={(e) => updateField('tours', 'travellers', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-2 flex items-end"><Button data-testid="search-tours-btn" onClick={() => handleSearch('tours')} className={searchBtnCls}><Search className="mr-2 h-5 w-5" /> SEARCH</Button></div>
                    </div>
                  </TabsContent>

                  <TabsContent value="visa" className="mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-5"><label className={labelCls}>Country</label><Input data-testid="visa-country" placeholder="Select destination country" value={forms.visa.country} onChange={(e) => updateField('visa', 'country', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-5"><label className={labelCls}>Travel Date</label><Input data-testid="visa-date" type="date" value={forms.visa.travel_date} onChange={(e) => updateField('visa', 'travel_date', e.target.value)} className={dateInputCls} /></div>
                      <div className="md:col-span-2 flex items-end"><Button data-testid="search-visa-btn" onClick={() => handleSearch('visa')} className={searchBtnCls}><Search className="mr-2 h-5 w-5" /> SEARCH</Button></div>
                    </div>
                  </TabsContent>

                  <TabsContent value="cruise" className="mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-5"><label className={labelCls}>Departure Port</label><Input data-testid="cruise-destination" placeholder="Select port" value={forms.cruise.destination} onChange={(e) => updateField('cruise', 'destination', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-3"><label className={labelCls}>Sailing Date</label><Input data-testid="cruise-date" type="date" value={forms.cruise.sail_date} onChange={(e) => updateField('cruise', 'sail_date', e.target.value)} className={dateInputCls} /></div>
                      <div className="md:col-span-2"><label className={labelCls}>Guests</label><Input data-testid="cruise-guests" type="number" min={1} value={forms.cruise.guests} onChange={(e) => updateField('cruise', 'guests', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-2 flex items-end"><Button data-testid="search-cruise-btn" onClick={() => handleSearch('cruise')} className={searchBtnCls}><Search className="mr-2 h-5 w-5" /> SEARCH</Button></div>
                    </div>
                  </TabsContent>

                  <TabsContent value="forex" className="mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-5"><label className={labelCls}>Currency</label><Input data-testid="forex-currency" placeholder="USD / EUR / GBP" value={forms.forex.currency} onChange={(e) => updateField('forex', 'currency', e.target.value.toUpperCase())} className={inputCls} /></div>
                      <div className="md:col-span-5"><label className={labelCls}>Amount</label><Input data-testid="forex-amount" type="number" min={1} value={forms.forex.amount} onChange={(e) => updateField('forex', 'amount', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-2 flex items-end"><Button data-testid="search-forex-btn" onClick={() => handleSearch('forex')} className={searchBtnCls}><Search className="mr-2 h-5 w-5" /> SEARCH</Button></div>
                    </div>
                  </TabsContent>

                  <TabsContent value="insurance" className="mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-4"><label className={labelCls}>Destination</label><Input data-testid="insurance-destination" placeholder="Domestic / International" value={forms.insurance.destination} onChange={(e) => updateField('insurance', 'destination', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-3"><label className={labelCls}>Travel Date</label><Input data-testid="insurance-date" type="date" value={forms.insurance.travel_date} onChange={(e) => updateField('insurance', 'travel_date', e.target.value)} className={dateInputCls} /></div>
                      <div className="md:col-span-3"><label className={labelCls}>Travellers</label><Input data-testid="insurance-travellers" type="number" min={1} value={forms.insurance.travellers} onChange={(e) => updateField('insurance', 'travellers', e.target.value)} className={inputCls} /></div>
                      <div className="md:col-span-2 flex items-end"><Button data-testid="search-insurance-btn" onClick={() => handleSearch('insurance')} className={searchBtnCls}><Search className="mr-2 h-5 w-5" /> SEARCH</Button></div>
                    </div>
                  </TabsContent>
                </div>
              </Tabs>
            </CardContent>
          </Card>

          {/* Recent searches chip row — directly below search card for conversion lift */}
          <RecentSearchesStrip />
        </div>
      </div>

      {/* Trending stays this week — data-driven hero rail. Sits between the
          search banner and the personal Recently-Viewed rails. */}
      <TrendingStays />

      {/* Recently viewed hotels + flights — homepage conversion rails */}
      <RecentlyViewedHotels />
      <RecentlyViewedFlights />

      {/* Travel Themes — curated discovery row (Phase B) */}
      <DiscoveryThemes />

      {/* Offers — horizontally scrollable */}
      <Section
        eyebrow="Limited time"
        title={accountType === 'personal' ? 'Exclusive Offers' : 'Business Deals'}
        sub={accountType === 'personal' ? 'Promotions, deals and special offers for you' : 'Special corporate rates and business travel discounts'}
        className="py-8 sm:py-12"
      >
        {offers.length === 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} h={192} />)}
          </div>
        ) : (
          <Carousel itemMinWidth={280} ariaLabel="offers">
            {offers.slice(0, 8).map((offer) => (
              <Card key={offer.id} data-testid={`offer-${offer.id}`} className="group hover:shadow-2xl transition-all duration-300 cursor-pointer border-0 overflow-hidden transform hover:-translate-y-1 h-full">
                <div className="relative h-44 sm:h-52 overflow-hidden">
                  <img src={offer.image} alt={offer.title} loading="lazy" className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30"></div>
                  <div className="absolute inset-0 p-4 sm:p-5 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <Badge className="bg-white text-red-600 font-bold shadow-lg">{offer.code}</Badge>
                      <div className="bg-white/20 backdrop-blur-sm p-1.5 sm:p-2 rounded-full">
                        <Percent className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold text-base sm:text-lg mb-1 sm:mb-2 text-white drop-shadow-lg leading-tight">{offer.title}</h3>
                      <p className="text-xs sm:text-sm text-white/90 mb-1.5 sm:mb-3 drop-shadow-md line-clamp-2">{offer.description}</p>
                      <p className="text-[10px] sm:text-xs text-white/80">Valid till {offer.validity}</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </Carousel>
        )}
      </Section>

      {/* Popular Destinations */}
      <Section
        eyebrow="Trending now"
        title="Handpicked Collections for You"
        sub="Explore curated lists of top destinations"
        className="py-8 sm:py-12"
      >
        {popularDestinations.length === 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} h={220} />)}
          </div>
        ) : (
          <Carousel itemMinWidth={240} ariaLabel="popular destinations">
            {popularDestinations.map((destination) => (
              <Card key={destination.id} data-testid={`destination-${destination.id}`} className="group overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer border-0 h-full">
                <div className="relative h-48 sm:h-60 overflow-hidden">
                  <img src={destination.image} alt={destination.city} loading="lazy" className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 text-white">
                    <h3 className="text-lg sm:text-2xl font-bold mb-0.5 sm:mb-1">{destination.city}</h3>
                    <p className="text-[11px] sm:text-sm text-gray-200 line-clamp-2">{destination.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </Carousel>
        )}
      </Section>

      <DiscoveryTestimonials />

      {/* Why Book with Us */}
      <div className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-gray-800 dark:to-gray-900 py-8 sm:py-12 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">Why Book with Elite Travo?</h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">We provide best-in-class service</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8">
            <div className="text-center bg-white dark:bg-gray-800 p-8 rounded-xl hover:shadow-lg transition-shadow duration-300">
              <div className="bg-sky-100 dark:bg-sky-900 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Percent className="h-8 w-8 text-sky-600 dark:text-sky-400" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">Lowest Price</h3>
              <p className="text-gray-600 dark:text-gray-400">We ensure low rates on hotel reservations, holiday packages and flights</p>
            </div>
            <div className="text-center bg-white dark:bg-gray-800 p-8 rounded-xl hover:shadow-lg transition-shadow duration-300">
              <div className="bg-green-100 dark:bg-green-900 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">Secure Payments</h3>
              <p className="text-gray-600 dark:text-gray-400">All your transactions are secure with highest levels of security</p>
            </div>
            <div className="text-center bg-white dark:bg-gray-800 p-8 rounded-xl hover:shadow-lg transition-shadow duration-300">
              <div className="bg-purple-100 dark:bg-purple-900 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Headphones className="h-8 w-8 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">24/7 Support</h3>
              <p className="text-gray-600 dark:text-gray-400">Get assistance 24/7 on any kind of travel related query</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
